import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Box,
  Container,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import LaunchIcon from '@material-ui/icons/Launch';
import { BackstageTheme } from '@backstage/theme';
import { LinkItem } from '../../types/links';
import { linksConfiguration } from '../../config/linksConfig';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  root: {
    padding: theme.spacing(4),
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    fontFamily: theme.typography.fontFamily,
  },
  header: {
    marginBottom: theme.spacing(5),
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
    },
  },
  title: {
    fontWeight: 700,
    fontSize: '2.5rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
    maxWidth: 600,
  },
  searchBox: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    '& .MuiTextField-root': {
      width: '100%',
      maxWidth: 700,
      '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        },
        '&.Mui-focused': {
          boxShadow: `0 4px 16px ${theme.palette.primary.main}25`,
        },
      },
    },
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.16)',
      borderColor: theme.palette.primary.light,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
  },
  cardIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
    fontSize: '2.2rem',
    flexShrink: 0,
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: '1.15rem',
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  description: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
  cardActions: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: `${theme.palette.grey[50]}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  launchButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'none',
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
      transform: 'translateY(-1px)',
    },
  },
  categoryChip: {
    fontSize: '0.75rem',
    height: 28,
    fontWeight: 500,
    borderRadius: 14,
    backgroundColor: `${theme.palette.grey[100]}`,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.grey[300]}`,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  noResults: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(6),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(4, 0),
  },
  noResultsIcon: {
    fontSize: '3rem',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(2),
  },
  cardsContainer: {
    marginTop: 20,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
  },
  logo: {
    width: '64px',
    height: 'auto',
    marginBottom: theme.spacing(2),
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
    [theme.breakpoints.up('md')]: {
      width: '72px',
      marginBottom: 0,
      marginRight: theme.spacing(3),
    },
  },
}));


export const ResourceCenter: React.FC = () => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLinks = useMemo(() => {
    if (!searchTerm.trim()) {
      return linksConfiguration.links;
    }

    const term = searchTerm.toLowerCase();
    return linksConfiguration.links.filter(link =>
      link.title.toLowerCase().includes(term) ||
      link.description.toLowerCase().includes(term) ||
      link.category.toLowerCase().includes(term) ||
      link.keywords.some(keyword => keyword.toLowerCase().includes(term))
    );
  }, [searchTerm]);

  const handleLaunch = (url: string, title: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Box className={classes.header}>
        <div className={classes.logoContainer}>
        <img
          src="logoidp.png"
          alt="BANKDEVX Logo"
          className={classes.logo}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'logo192.png';
          }}
        />
        <div>
          <Typography variant="h4" component="h1" className={classes.title}>
            Corporate Resource Center
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Unified portal to access all enterprise tools, development platforms, and technical resources of the organization
          </Typography>
        </div>
      </div>
      </Box>

      <Box className={classes.searchBox}>
        <TextField
          variant="outlined"
          placeholder="Search by name, description, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filteredLinks.length === 0 ? (
        <Box className={classes.noResults}>
          <Typography variant="h6">No results found</Typography>
          <Typography variant="body2">
            Try different search terms
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3} className={classes.cardsContainer}>
          {filteredLinks.map((link) => {
            const IconComponent = link.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={link.id}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <Box className={classes.cardHeader}>
                      <IconComponent className={classes.cardIcon} />
                      <Typography variant="h6" className={classes.cardTitle}>
                        {link.title}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      className={classes.description}
                      paragraph
                    >
                      {link.description}
                    </Typography>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Chip 
                      label={link.category} 
                      size="small" 
                      className={classes.categoryChip}
                    />
                    <Button
                      variant="contained"
                      className={classes.launchButton}
                      endIcon={<LaunchIcon />}
                      onClick={() => handleLaunch(link.url, link.title)}
                      size="small"
                    >
                      Open
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default ResourceCenter;
